import classNames from "classnames";
import { addHours, differenceInCalendarDays } from "date-fns";
import React, { useState } from "react";
import { DeadlineStepToTextMap } from "../../common/types/Deadline";
import { RequestStatusMap } from "../../common/types/RequestStatus";
import { toDateTimeString } from "../../utils/time.util";
import DeadlineTag from "../Common/DeadlineTag";
import HandleClicksOutside from "../HandleClicksOutside";
import "./RequestStatusSelector.css";
import type { ChangeRequestStatusRequest } from "@albertine/types/openapi/lmt";
import type { Request } from "../../common/types/Request";
import {
    requestIsActionable,
    requestNoFurtherAction,
    requestOutcome,
} from "../../common/utils/requests.util";
import { fromTimestampToDate } from "../../utils/timestamp.util";
import { Flex } from "../Common/Flex";
import Icon, { PictogramIcon } from "../Common/Icon";
import LocalDateTimePicker, { roundToPickerStep } from "../Common/LocalDateTimePicker";
import Tag from "../Common/Tag";
import TimeTag from "../Common/TimeTag";
import {
    type ActionOption,
    buildChangeRequestStatusRequest,
    type Deadline,
    getNextSteps,
    getRequestStatusChange,
    shouldAskForBookingResolution,
} from "./RequestStatusSelector.util";

type Props = {
    request: Request;
    requestHasConfirmedBookings: boolean;
    requestHasCancelledBookings: boolean;
    deadline: Deadline | null | undefined;
    closeAt: Date | null | undefined;
    beforeOnClick: () => boolean;
    onChange: (change: ChangeRequestStatusRequest) => void;
};

const RequestStatusSelector = function RequestStatusSelector(props: Props) {
    const {
        request,
        deadline,
        beforeOnClick,
        onChange,
        requestHasConfirmedBookings,
        requestHasCancelledBookings,
        closeAt,
    } = props;

    const currentStatus = request.status;

    const [showOptions, setShowOptions] = useState<boolean>(false);
    const [action, setAction] = useState<ActionOption | undefined>(undefined);

    const status = RequestStatusMap[currentStatus];

    const askForBookingResolution = shouldAskForBookingResolution(request);
    const actionOptions = getNextSteps(
        action,
        currentStatus,
        requestHasConfirmedBookings,
        requestHasCancelledBookings,
        askForBookingResolution,
    );

    const [specificDeadlineDate, setSpecificDeadlineDate] = useState<
        Date | undefined
    >();

    const onClose = () => {
        setShowOptions(false);
        setAction(undefined);
        setSpecificDeadlineDate(undefined);
    };

    const onSelectAction = (selectedAction: ActionOption) => {
        const change = getRequestStatusChange(request, selectedAction, action);

        if (change.change === "status_changed" && change.newStatus) {
            onChange(buildChangeRequestStatusRequest(change.nextAction));
            onClose();
        } else {
            setAction(change.nextAction);
        }
    };

    // close request when booking start/end time is passed + 6 hours
    const daysLeftToCloseRequest = closeAt
        ? differenceInCalendarDays(addHours(closeAt, 6), Date.now())
        : 0;

    const notVisibleForMember = currentStatus === "draft";

    const daysLeftToClose =
        requestNoFurtherAction(request) && daysLeftToCloseRequest > 0
            ? daysLeftToCloseRequest
            : undefined;

    return (
        <HandleClicksOutside
            className="request-status-selector"
            onClickOutside={onClose}
            disabled={!showOptions}
        >
            <div
                className={classNames({
                    "request-status-selector__button": true,
                    "request-status-selector__button-active":
                        currentStatus === "new" ||
                        currentStatus === "open" ||
                        currentStatus === "confirmed",
                    "pictogram-icon--hover": true,
                })}
                onClick={() => {
                    if (beforeOnClick && !beforeOnClick()) return;
                    if (showOptions) onClose();
                    else setShowOptions(true);
                }}
            >
                <div>
                    <div className="request-status-selector__status">
                        {status}
                        {requestOutcome(request) && (
                            <>
                                <span>·</span>
                                <span className="request-status-selector__outcome">
                                    {requestOutcome(request)}
                                </span>
                            </>
                        )}
                    </div>
                    <div className="request-status-selector__step-description">
                        {request.nextDeadlineStep && (
                            <Tag.XSmall.N90
                                className="request-status-selector__deadline-description"
                                disabled={
                                    requestIsActionable(request) === "non-actionable"
                                }
                            >
                                {DeadlineStepToTextMap[request.nextDeadlineStep]}
                            </Tag.XSmall.N90>
                        )}
                        <DeadlineTag deadlineAt={deadline?.nextDeadlineAt} />
                        {notVisibleForMember && <div>Not visible to member</div>}
                        {currentStatus === "new" && (
                            <TimeTag
                                label="Added"
                                time={fromTimestampToDate(request.createdAt)}
                            />
                        )}
                        {requestNoFurtherAction(request) && (
                            <Tag.XSmall.N90 disabled>No further action</Tag.XSmall.N90>
                        )}
                        {daysLeftToClose && <div>Closes in {daysLeftToClose} days</div>}
                    </div>
                </div>

                {showOptions ? (
                    <PictogramIcon.UnfoldLess.Small.N99 />
                ) : (
                    <PictogramIcon.UnfoldMore.Small.N99 />
                )}
            </div>

            {showOptions && (
                <div className="request-status-selector__deadline">
                    <div>
                        {actionOptions?.question && (
                            <div
                                className="request_status-selector__step-question-container"
                                onClick={() => {
                                    if (action && "previous" in action)
                                        setAction(action.previous);
                                    else setShowOptions(false);
                                }}
                            >
                                <Icon.ArrowBack.Small />
                                <span className="request_status-selector__step-question">
                                    {actionOptions.question}
                                </span>
                            </div>
                        )}

                        {actionOptions?.options.map((option, index) => {
                            const IconComponent =
                                option.icon && Icon[option.icon]
                                    ? Icon[option.icon]
                                    : null;
                            return (
                                <Flex.Row
                                    key={`${option.action}__${index}`}
                                    onClick={() => {
                                        onSelectAction(option);
                                    }}
                                    className="request_status-selector__option"
                                >
                                    <Flex.Row gap="medium">
                                        {IconComponent && <IconComponent.Small />}

                                        {option.label}
                                    </Flex.Row>
                                    {option.isInternal && (
                                        <Icon.VpnKey.XSmall.N60.Fill />
                                    )}
                                </Flex.Row>
                            );
                        })}

                        {action?.action === "update_next_step_deadline_selection" && (
                            <div>
                                {specificDeadlineDate ? (
                                    <>
                                        <Flex.Row className="request_status-selector__option request_status-selector__option-last">
                                            <Flex.Row>
                                                <Icon.CalendarMonth.Small />
                                                <span>
                                                    {toDateTimeString(
                                                        specificDeadlineDate,
                                                    )}
                                                </span>
                                            </Flex.Row>
                                            <Icon.ArrowForward.Small />
                                        </Flex.Row>
                                        <LocalDateTimePicker
                                            initialDate={specificDeadlineDate}
                                            onChange={setSpecificDeadlineDate}
                                            onConfirm={(specificDate) => {
                                                onSelectAction({
                                                    ...action,
                                                    action: "update_next_step_deadline_time_selection",
                                                    nextDeadlineAt: specificDate,
                                                });
                                            }}
                                            onCancel={() => {
                                                setSpecificDeadlineDate(undefined);
                                            }}
                                        />
                                    </>
                                ) : (
                                    <Flex.Row
                                        justifyContent="space-between"
                                        className="request_status-selector__option request_status-selector__option-last"
                                        onClick={() => {
                                            setSpecificDeadlineDate(
                                                roundToPickerStep(new Date()),
                                            );
                                        }}
                                    >
                                        <Flex.Row gap="medium">
                                            <Icon.CalendarMonth.Small />
                                            <span>Select specific date</span>
                                        </Flex.Row>
                                    </Flex.Row>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </HandleClicksOutside>
    );
};

export default RequestStatusSelector;
